import { h } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useDispatch, useSelector } from "react-redux";

import i18n from "../../i18n.js";
import talentsApi from "../../api/talents";
import { sendError } from "../../errors.js";
import Tag from "../Tag.jsx";
import TalentFilter from "../TalentFilter.jsx";
import {
  selectCurrentFilters,
  setTalentFilters,
  toggleTalentFilter,
} from "../../store";
import tracking from "../../tracking/tracking";

export default function FilterBar({ id }) {
  const filters = useSelector((state) => state.talentPool.filters || {});
  const currentFilters = useSelector(selectCurrentFilters);

  const [isLoading, setIsLoading] = useState(false);
  const [askedFilters, setAskedFilters] = useState("");
  const [lastCurrentFilters, setLastCurrentFilters] = useState(currentFilters);

  const dispatch = useDispatch();

  useEffect(async () => {
    if (currentFilters.length !== lastCurrentFilters.length)
      setLastCurrentFilters(currentFilters);
    const filters = {
      areaOfWork: currentFilters
        .filter((f) => f.type === "areaOfWork")
        .map((f) => f.value)
        .join(","),
    };
    const askForFilters = JSON.stringify(filters);
    if (askedFilters === askForFilters) return;

    if (isLoading) return;

    setIsLoading(true);
    try {
      const result = await talentsApi.fetchFilters(filters);
      dispatch(setTalentFilters(result));
    } catch (err) {
      sendError(err);
      // TODO: Show something here as well
    }
    setAskedFilters(askForFilters);
    setIsLoading(false);
  }, [currentFilters]);

  function toggleActiveFilter(changedItem) {
    tracking.track("filterBar.change", {
      name: changedItem.type,
      value: changedItem.value,
      selected: changedItem.selected,
    });

    dispatch(toggleTalentFilter({ id, changedItem }));
  }
  function clearFilters() {
    currentFilters.forEach((filter) => {
      dispatch(toggleTalentFilter({ id, changedItem: filter }));
    });
  }

  function hasAreaOfWorkFilter() {
    return currentFilters.findIndex((x) => x.type === "areaOfWork") > -1;
  }

  // TODO: Add empty state to ServerPaginated
  return (
    <div class="talent-pool-filter-bar">
      <label class="talent-pool-filter-bar__label">
        {i18n.t("pages.talent_pool.filter.label")}
      </label>
      <div class="talent-pool-filter-bar__filters">
        <div class="talent-pool-filter-bar__filter-row">
          {filters.areaOfWork && filters.areaOfWork.length > 0 && (
            <TalentFilter
              name={i18n.t("pages.talent_pool.filter.area_of_work")}
              items={filters.areaOfWork}
              onChange={toggleActiveFilter}
              triggerClass="mr-16"
              count={filters.areaOfWork.filter((x) => x.selected).length}
              noSorting
            />
          )}
          {hasAreaOfWorkFilter() &&
            filters.workRole &&
            filters.workRole.length > 0 && (
              <TalentFilter
                filtered={filters.workRole.length > 7}
                name={i18n.t("pages.talent_pool.filter.work_role")}
                items={filters.workRole}
                onChange={toggleActiveFilter}
                triggerClass="mr-16"
                count={filters.workRole.filter((x) => x.selected).length}
              />
            )}
          {filters.skills && filters.skills.length > 0 && (
            <TalentFilter
              filtered={filters.skills.length > 7}
              name={i18n.t("pages.talent_pool.filter.skill")}
              items={filters.skills}
              onChange={toggleActiveFilter}
              triggerClass="mr-16"
              count={filters.skills.filter((x) => x.selected).length}
            />
          )}
          {filters.regions && filters.regions.length > 0 && (
            <TalentFilter
              withPromoted
              customPromotedSorting={(a, b) => (a.value === "DE" ? -1 : 1)}
              filtered={filters.regions.length > 7}
              name={i18n.t("pages.talent_pool.filter.region")}
              items={filters.regions}
              onChange={toggleActiveFilter}
              triggerClass="mr-16"
              count={filters.regions.filter((x) => x.selected).length}
            />
          )}
          {filters.company && filters.company.length > 0 && (
            <TalentFilter
              withPromoted
              filtered={filters.company.length > 7}
              name={i18n.t("pages.talent_pool.filter.company")}
              items={filters.company}
              onChange={toggleActiveFilter}
              triggerClass="mr-16"
              count={filters.company.filter((x) => x.selected).length}
            />
          )}
          {filters.state && filters.state.length > 0 && (
            <TalentFilter
              filtered={filters.state.length > 7}
              name={i18n.t("pages.talent_pool.filter.state")}
              items={filters.state}
              onChange={toggleActiveFilter}
              triggerClass="mr-16"
              count={filters.state.filter((x) => x.selected).length}
            />
          )}
          {filters.language && filters.language.length > 0 && (
            <TalentFilter
              withPromoted
              filtered
              name={i18n.t("pages.talent_pool.filter.language")}
              items={filters.language}
              onChange={toggleActiveFilter}
              triggerClass="mr-16"
              count={filters.language.filter((x) => x.selected).length}
            />
          )}
          {filters.yearsOfExperience &&
            filters.yearsOfExperience.length > 0 && (
              <TalentFilter
                name={i18n.t("pages.talent_pool.filter.years_of_experience")}
                items={filters.yearsOfExperience}
                onChange={toggleActiveFilter}
                triggerClass="mr-16"
                count={
                  filters.yearsOfExperience.filter((x) => x.selected).length
                }
              />
            )}
        </div>
        <div class="talent-pool-filter-bar__active-filters">
          {currentFilters.map((filter) => (
            <Tag
              grey
              onClick={() => toggleActiveFilter(filter)}
              iconBack="close"
              class="mr-12 mb-8"
            >
              {filter.text}
            </Tag>
          ))}

          {currentFilters.length >= 2 && (
            <span class="tag tag-link">
              <a href="#" onClick={clearFilters}>
                {i18n.t("pages.talent_pool.clear_filters")}
              </a>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
